/**
 * Job Content Interaction Script
 * Handles real file uploads via Google Apps Script (Custom Backend).
 */

window.initJobContent = function(sectionId) {
    const section = document.getElementById(sectionId);
    if (!section) return;

    // --- 1. Share Button Logic ---
    const shareBtn = section.querySelector('.job-share-copy');
    if (shareBtn) {
        shareBtn.addEventListener('click', (e) => {
            e.preventDefault();
            navigator.clipboard.writeText(window.location.href);
            
            // Visual feedback
            const icon = shareBtn.querySelector('svg');
            if(icon) {
                 const originalSvg = icon.innerHTML;
                 icon.innerHTML = '<polyline points="20 6 9 17 4 12"></polyline>';
                 setTimeout(() => {
                     icon.innerHTML = originalSvg;
                 }, 2000);
            }
        });
    }

    // --- 2. File Upload UI Logic ---
    // Scoped IDs (Note: IDs in Liquid should ideally be unique per section, 
    // but here we scope search to section just in case, though ID lookup is global. 
    // For strict correctness, we assume the Liquid generates unique IDs or we query by class if possible.
    // Given current Liquid uses static IDs, we'll try to find them within the section to be safe,
    // though duplicate IDs on a page is HTML invalid.)
    const fileInput = section.querySelector('#job-resume-file');
    const fileTrigger = section.querySelector('#job-file-trigger');
    const fileLabel = section.querySelector('#job-file-label');
    const fileIcon = section.querySelector('#job-file-icon');

    if (fileInput && fileTrigger) {
        // Trigger hidden input on click
        fileTrigger.addEventListener('click', () => {
            fileInput.click();
        });

        // Update UI when file is selected
        fileInput.addEventListener('change', () => {
            if (fileInput.files.length > 0) {
                const file = fileInput.files[0];
                const MAX_SIZE = 5 * 1024 * 1024; // 5MB

                if (file.size > MAX_SIZE) {
                    alert('File is too large. Please upload a file smaller than 5MB.');
                    fileInput.value = ''; // Clear input
                    resetFileUI();
                    return;
                }

                const fileName = file.name;
                fileLabel.textContent = fileName;
                fileLabel.style.color = '#111827'; // Darker text
                fileLabel.style.fontWeight = '600';
                
                // Change icon to a file-with-check (swap the inline-SVG's contents)
                fileIcon.innerHTML = '<path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5z"></path><polyline points="14 2 14 8 20 8"></polyline><path d="m9 15 2 2 4-4"></path>';
                fileIcon.style.color = '#16A34A'; // Green
                
                // Remove "hint" text if existing
                const hint = fileTrigger.querySelector('p:nth-child(3)');
                if (hint) hint.style.display = 'none';
            }
        });
    }

    // --- 3. Form Submission Logic (Google Apps Script) ---
    const form = section.querySelector('#job-application-form');
    const statusContainer = section.querySelector('#job-form-status');
    const submitBtn = section.querySelector('#job-submit-btn');
    const spinner = submitBtn ? submitBtn.querySelector('.job-apply-spinner') : null;
    const btnText = submitBtn ? submitBtn.querySelector('span') : null;

    if (form) {
        form.addEventListener('submit', async (e) => {
            e.preventDefault();

            const scriptUrl = form.getAttribute('data-script-url');
            if (!scriptUrl) {
                showStatus('error', 'Configuration Error: Google Script URL is missing in theme settings.');
                return;
            }

            // Start Loading State
            setLoading(true);

            try {
                // 1. Validation: Turnstile Captcha
                // Turnstile global object
                if (typeof turnstile !== 'undefined') {
                    const captchaToken = turnstile.getResponse();
                    if (!captchaToken) {
                        showStatus('error', 'Please verify that you are human.');
                        setLoading(false);
                        return;
                    }
                }

                // 2. Prepare Data (Nested JSON Structure)
                const formData = new FormData(form);
                const file = fileInput.files[0];

                // Read File as Base64
                let fileData = null;
                if (file) {
                    fileData = await readFileAsBase64(file);
                }

                // Construct Object matching user request
                const payload = {
                    user: {
                        name: formData.get('name'),
                        email: formData.get('email'),
                        phone: formData.get('phone'),
                        cover_letter: formData.get('cover_letter'),
                        job_handle: form.getAttribute('data-job-handle') || document.title,
                        metaobject_type: form.getAttribute('data-metaobject-type') 
                    },
                    file: file ? {
                        name: file.name,
                        type: file.type,
                        data: fileData // base64 string
                    } : null,
                    security: {
                         // Turnstile token if available
                        turnstile_token: (typeof turnstile !== 'undefined') ? turnstile.getResponse() : null
                    }
                };

                // 3. Send Request (JSON)
                const response = await fetch(scriptUrl, {
                    method: 'POST',
                    body: JSON.stringify(payload),
                    headers: { "Content-Type": "application/json" },
                });

                // 4. Handle Response
                if (response.ok) {
                    // Try to parse JSON, but if n8n returns simple text (like "Webhook received!"), handle it gracefully.
                    let result = {};
                    try {
                        result = await response.json();
                    } catch (e) {
                        console.warn('Server returned non-JSON response:', e);
                        // Assume success if status is 200 but body is not JSON
                        result = { status: 'success' };
                    }

                    if (result.status === 'success' || !result.status) {
                         // If explicit success OR lax success (no status field)
                        const successMsg = form.getAttribute('data-success-message') 
                            || 'Thanks for contacting us. We\'ll get back to you as soon as possible.';
                        showStatus('success', successMsg);
                        form.reset();
                        resetFileUI();
                        // Reset Captcha for next submission
                        if (window.turnstile) turnstile.reset();
                    } else {
                        throw new Error(result.message || 'Server returned an error.');
                    }
                } else {
                     // Status is not 200-299 (e.g. 500 or 404)
                     if (response.status === 413) {
                         throw new Error('File is too large. Server rejected it.');
                     }
                     throw new Error('Server Error'); 
                }

            } catch (error) {
                console.error('Submission Error:', error);
                
                // User-friendly Error Massage
                let displayMessage = 'An unexpected error occurred. Please try again or email us directly.';
                
                if (error.message.includes('File is too large')) {
                    displayMessage = 'File is too large. Please upload a file smaller than 5MB.';
                } else if (error.message.includes('NetworkError') || error.message.includes('Failed to fetch')) {
                     // Often CORS / Payload Too Large appears as NetworkError
                    displayMessage = 'Network Error. Your file might be too large (Max 5MB).';
                }
                
                showStatus('error', displayMessage);
            } finally {
                setLoading(false);
            }
        });
    }

    // --- Helpers ---

    function setLoading(isLoading) {
        if (!submitBtn) return;
        submitBtn.disabled = isLoading;
        if (isLoading) {
            submitBtn.style.opacity = '0.7';
            if (spinner) spinner.style.display = 'inline-block';
            if (btnText) btnText.textContent = 'Sending...';
        } else {
            submitBtn.style.opacity = '1';
            if (spinner) spinner.style.display = 'none';
            // Reset text (we might want to keep "Submitted" if success, but generic reset here)
            if (btnText) btnText.textContent = 'Submit Application'; 
        }
    }

    function showStatus(type, message) {
        if (!statusContainer) return;
        statusContainer.style.display = 'block';
        const statusIcon = type === 'success'
            ? '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" style="vertical-align:-2px"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>'
            : '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" style="vertical-align:-2px"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3z"></path><line x1="12" y1="9" x2="12" y2="13"></line><line x1="12" y1="17" x2="12.01" y2="17"></line></svg>';
        statusContainer.innerHTML = `${statusIcon} ${message}`;
        
        statusContainer.className = `form__message ${type === 'success' ? 'form-status-success' : 'form-status-error'}`;
        
        // Scroll to message
        statusContainer.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    function resetFileUI() {
        if (fileInput) fileInput.value = ''; // clear file
        if (fileLabel) {
            fileLabel.textContent = 'Click to upload or drag & drop'; // reset text
            fileLabel.style.color = '#6B7280';
            fileLabel.style.fontWeight = '400';
        } 
        if (fileIcon) {
            fileIcon.innerHTML = '<path d="M4 14.899A7 7 0 1 1 15.71 8h1.79a4.5 4.5 0 0 1 2.5 8.242"></path><path d="M12 12v9"></path><path d="m16 16-4-4-4 4"></path>';
            fileIcon.style.color = '#9CA3AF';
        }
        const hint = section.querySelector('.job-apply-upload p:nth-child(3)');
        if (hint) hint.style.display = 'block';
    }

    function readFileAsBase64(file) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                // Return only the Base64 string, stripping the Data URL prefix
                const content = reader.result.toString();
                const base64 = content.indexOf(',') !== -1 ? content.split(',')[1] : content;
                resolve(base64);
            }; 
            reader.onerror = error => reject(error);
            reader.readAsDataURL(file);
        });
    }

};
